import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CameraOff, Keyboard } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { classifyScan, isSerialCandidate } from '@/lib/barcodeKinds';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

// How long to keep looking for a better code before accepting this one.
const SECONDARY_WAIT_MS = 1500;
// A label stays in view for a while, so the same code keeps being decoded.
const REPEAT_WINDOW_MS = 2000;

/**
 * Camera barcode / QR scanner for the browser (phone or computer).
 *
 * - Uses the back camera when there is one.
 * - The text box below the camera also catches USB / Bluetooth hand
 *   scanners, which "type" the code and press Enter.
 * - Calls `onDetected(code, { secondary })` once and stops the camera.
 *   Box labels carry several codes, so a code that is not a product barcode
 *   only wins if no product barcode shows up shortly after (`secondary`).
 *   Web address QR codes are skipped.
 * - `mode="serial"` looks for serial numbers instead: product barcodes are the
 *   same on every box, so they are skipped. With `continuous` the camera stays
 *   on for the next box and codes in `known` are not reported again.
 */
export function BarcodeScannerDialog({
  open, onClose, onDetected, mode = 'product', continuous = false, known = [], title, help, children,
}) {
  const { t } = useTranslation('quotes');
  const handlerRef = useRef(onDetected);
  handlerRef.current = onDetected;
  const knownRef = useRef(known);
  knownRef.current = known;
  const recentRef = useRef({ code: '', at: 0 });
  const videoRef = useRef(null);
  const controlsRef = useRef(null);
  const doneRef = useRef(false);
  const timerRef = useRef(null);
  const [cameraError, setCameraError] = useState(null);
  const [linkSeen, setLinkSeen] = useState(false);
  const [manual, setManual] = useState('');

  const stop = () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
    try { controlsRef.current?.stop(); } catch { /* already stopped */ }
    controlsRef.current = null;
  };

  const finish = (code, info = {}) => {
    const value = String(code || '').trim();
    if (!value || doneRef.current) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
    if (continuous) {
      setManual('');
    } else {
      doneRef.current = true;
      stop();
    }
    try { navigator.vibrate?.(80); } catch { /* not supported */ }
    handlerRef.current(value, info);
  };

  const finishSerial = (text) => {
    const value = String(text || '').trim();
    const now = Date.now();
    const recent = recentRef.current;
    if (knownRef.current.includes(value) || (recent.code === value && now - recent.at < REPEAT_WINDOW_MS)) return;
    recentRef.current = { code: value, at: now };
    finish(value);
  };

  const handleSerial = (text, format) => {
    const kind = classifyScan(text, format);
    if (kind === 'link') {
      setLinkSeen(true);
    } else if (kind === 'other') {
      if (isSerialCandidate(text)) {
        finishSerial(text);
      } else if (!timerRef.current) {
        // Digits only: could be a material code, so give a real serial a moment.
        timerRef.current = setTimeout(() => { timerRef.current = null; finishSerial(text); }, SECONDARY_WAIT_MS);
      }
    }
  };

  const handleResult = (text, format) => {
    const kind = classifyScan(text, format);
    if (kind === 'link') {
      setLinkSeen(true);
    } else if (kind !== 'other') {
      finish(text);
    } else if (!timerRef.current) {
      timerRef.current = setTimeout(() => finish(text, { secondary: true }), SECONDARY_WAIT_MS);
    }
  };

  useEffect(() => {
    if (!open) return undefined;
    doneRef.current = false;
    setCameraError(null);
    setManual('');
    setLinkSeen(false);
    recentRef.current = { code: '', at: 0 };
    const onResult = mode === 'serial' ? handleSerial : handleResult;
    let cancelled = false;

    (async () => {
      if (!navigator.mediaDevices?.getUserMedia) {
        setCameraError('unsupported');
        return;
      }
      try {
        const { BarcodeFormat, BrowserMultiFormatReader } = await import('@zxing/browser');
        if (cancelled) return;
        const reader = new BrowserMultiFormatReader(undefined, { delayBetweenScanAttempts: 150 });
        // wait one frame so the <video> inside the dialog is mounted
        await new Promise((r) => setTimeout(r, 50));
        if (cancelled || !videoRef.current) return;
        const controls = await reader.decodeFromConstraints(
          { video: { facingMode: { ideal: 'environment' } }, audio: false },
          videoRef.current,
          (result) => { if (result) onResult(result.getText(), BarcodeFormat[result.getBarcodeFormat()]); },
        );
        if (cancelled) controls.stop();
        else controlsRef.current = controls;
      } catch (err) {
        if (!cancelled) {
          setCameraError(err?.name === 'NotAllowedError' ? 'denied' : 'failed');
        }
      }
    })();

    return () => {
      cancelled = true;
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { stop(); onClose(); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title || t('stock.scan.title')}</DialogTitle>
          <DialogDescription>{help || t('stock.scan.help')}</DialogDescription>
        </DialogHeader>

        {cameraError ? (
          <div className="flex flex-col items-center gap-2 rounded-xl bg-slate-50 p-6 text-center text-sm text-slate-600" data-testid="scan-camera-error">
            <CameraOff className="h-8 w-8 text-slate-400" />
            {t(`stock.scan.cameraError.${cameraError}`)}
          </div>
        ) : (
          <div className="relative overflow-hidden rounded-xl bg-black aspect-[4/3]">
            <video ref={videoRef} className="h-full w-full object-cover" muted playsInline data-testid="scan-video" />
            <div className="pointer-events-none absolute inset-x-8 top-1/2 h-24 -translate-y-1/2 rounded-lg border-2 border-white/80" />
          </div>
        )}

        {linkSeen && (
          <p className="text-sm text-amber-700" data-testid="scan-link-hint">{t('stock.scan.linkHint')}</p>
        )}

        <form
          onSubmit={(e) => { e.preventDefault(); finish(manual); }}
          className="flex gap-2"
        >
          <div className="relative flex-1">
            <Keyboard className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#94A3B8]" />
            <Input
              value={manual}
              onChange={(e) => setManual(e.target.value)}
              placeholder={t('stock.scan.manualPlaceholder')}
              className="pl-9"
              autoFocus={!!cameraError}
              data-testid="scan-manual-input"
            />
          </div>
          <Button type="submit" variant="outline" disabled={!manual.trim()}>{t(continuous ? 'service.serial.add' : 'stock.scan.find')}</Button>
        </form>

        {children}
      </DialogContent>
    </Dialog>
  );
}
